import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { MousePointerClick, Wallet, FileText, Coins, Sparkles, Link2, CalendarDays, Target } from "lucide-react";
import { PageHeader, StatCard, Spinner, EthicsNote } from "../components/common";
import { Progress } from "../components/ui/progress";
import { api, brl, fmtDate, channelLabel, shortUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Panel = ({ title, icon: Icon, children, testid, className = "" }) => (
  <div className={`card-lux p-5 rise rise-3 ${className}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-4"><Icon className="h-4 w-4 text-amber-400" /><h3 className="font-bold text-base">{title}</h3></div>
    {children}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;

  return (
    <div>
      <PageHeader eyebrow="Painel de desempenho" title={`Olá, ${user.name.split(" ")[0]}`} desc="Métricas reais dos seus links, conteúdos e comissões registradas."
        action={<Link to="/gerador" className="btn-gold rounded-full px-5 h-11 inline-flex items-center gap-2 text-sm" data-testid="dashboard-generate-cta"><Sparkles className="h-4 w-4" />Gerar conteúdo</Link>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard label="Cliques totais" value={d.clicks_total} hint={`${d.clicks_7d} nos últimos 7 dias`} icon={MousePointerClick} testid="kpi-clicks" delay={1} />
        <StatCard label="Comissões recebidas" value={brl(d.commission_earned)} hint={`Pendente: ${brl(d.commissions.pendente?.total)}`} icon={Wallet} testid="kpi-commissions" delay={2} />
        <StatCard label="Conteúdos" value={d.counts.contents} hint={`${d.counts.products} produtos • ${d.counts.links} links`} icon={FileText} testid="kpi-contents" delay={3} />
        <StatCard label="Créditos de IA" value={d.credits} hint={`Plano ${user.plan_info?.name}`} icon={Coins} testid="kpi-credits" delay={4} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <Panel title="Cliques x Comissões (14 dias)" icon={MousePointerClick} testid="dashboard-chart" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.series} margin={{ left: -20, right: 8 }}>
                <defs>
                  <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#D4AF37" stopOpacity={0.5} /><stop offset="1" stopColor="#D4AF37" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gm" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#10B981" stopOpacity={0.4} /><stop offset="1" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                <YAxis stroke="#71717A" fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#121215", border: "1px solid #3f3f46", borderRadius: 12 }} />
                <Area type="monotone" dataKey="cliques" stroke="#D4AF37" fill="url(#gc)" strokeWidth={2} />
                <Area type="monotone" dataKey="comissoes" name="comissões (R$)" stroke="#10B981" fill="url(#gm)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Metas" icon={Target} testid="dashboard-goals">
          {d.goals.length === 0 ? <p className="text-sm text-zinc-500">Nenhuma meta. <Link to="/metas" className="text-amber-400">Criar meta</Link></p> :
            <div className="space-y-4">{d.goals.map((g) => (
              <div key={g.id}><div className="flex justify-between text-xs mb-1.5"><span className="text-zinc-300">{g.title}</span><span className="text-amber-300 font-mono">{g.progress}%</span></div><Progress value={g.progress} className="h-2 bg-zinc-800" /></div>
            ))}</div>}
        </Panel>
        <Panel title="Links mais clicados" icon={Link2} testid="dashboard-top-links">
          {d.top_links.length === 0 ? <p className="text-sm text-zinc-500">Crie seu primeiro link rastreável.</p> :
            d.top_links.map((l) => (
              <div key={l.id} className="flex justify-between items-center py-2 border-b border-zinc-800/70 last:border-0">
                <div className="min-w-0"><div className="text-sm truncate">{l.title}</div><div className="text-[11px] text-zinc-500 font-mono truncate">{shortUrl(l.code)}</div></div>
                <span className="badge-gold shrink-0">{l.clicks}</span>
              </div>
            ))}
        </Panel>
        <Panel title="Próximas publicações" icon={CalendarDays} testid="dashboard-upcoming">
          {d.upcoming.length === 0 ? <p className="text-sm text-zinc-500">Nada agendado. <Link to="/calendario" className="text-amber-400">Abrir calendário</Link></p> :
            d.upcoming.map((e) => (
              <div key={e.id} className="flex justify-between py-2 border-b border-zinc-800/70 last:border-0 text-sm">
                <span className="truncate">{e.title}</span><span className="text-zinc-500 text-xs shrink-0 ml-2">{fmtDate(e.date)} • {channelLabel(e.channel)}</span>
              </div>
            ))}
        </Panel>
        <Panel title="Conteúdos por canal" icon={FileText} testid="dashboard-by-channel">
          {d.by_channel.length === 0 ? <p className="text-sm text-zinc-500">Gere conteúdos com IA para ver a distribuição.</p> :
            d.by_channel.map((c) => (
              <div key={c.channel} className="flex justify-between py-1.5 text-sm"><span className="text-zinc-300">{channelLabel(c.channel)}</span><span className="font-mono text-amber-300">{c.count}</span></div>
            ))}
        </Panel>
      </div>
      <div className="mt-6"><EthicsNote>Todos os números exibidos vêm de cliques reais nos seus links e de comissões registradas por você a partir dos relatórios oficiais. Não geramos nem inflamos métricas.</EthicsNote></div>
    </div>
  );
}
